import axios from "axios";
import React, { memo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Papa from "papaparse";
import "./PromptNode.css";
import Checkbox from "../../../../../../Components/CheckBox/Checkbox";
import CustomInput from "../../../../../../Components/Input/CustomInput";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { sendBugReport } from "../../../../../../utils/sendBugReport";
import { Clear, InfoOutlined } from "@mui/icons-material";
import {
  childNodeNavText,
  nodeInputStyles,
  topNavNode,
} from "../GeneralNodeStyles";
import {
  deleteButtonStyles,
  deleteIconStyles,
  primaryButtonStyles,
} from "../../../../../../Constants/buttonStyles";
import theme, {
  createLLMTypography,
  scrollbarStyles,
} from "../../../../../../Constants/theme";
import { dialogStyles } from "../../../../../../Constants/dialogStyles";
import { tableStyles } from "../../../../../../Constants/tableStyles";
import { generationDefault } from "../../../../../../utils/globalBotData";

const PromptNode = memo(
  ({
    data,
    initial_prompt,
    setInitial_prompt,
    LLMFoundational,
    handleOpenSideBar,
  }) => {
    const { botData, setBotData, nodeId } = data;
    const { user } = useSelector((state) => state.auth);
    const organization = useSelector((state) => state.organization);
    const [loader, set_loader] = useState(false);
    const dispatch = useDispatch();
    const [fewShotQA, setFewShotQA] = useState([]);

    const [csvData, setCsvData] = useState({});
    const [dataset, setDataset] = useState(
      botData?.dynamicShot_dataset?.[0]?.name || ""
    );
    const [datasetName, setDatasetName] = useState(
      botData?.dynamicShot_dataset?.[0]?.name || ""
    );
    const [openDatasetModal, setOpenDatasetModal] = useState(false);

    const [llm_prompts, set_llm_prompts] = useState(
      botData?.workflow?.[`${nodeId}`]?.llm_params?.prompts ||
        generationDefault.llm_params.prompts
    );

    const [resetInput, setResetInput] = useState(false);
    const [fewShotReset, setfewShotReset] = useState(false);
    const dynamicRef = useRef(null);
    const fewShotRef = useRef(null);

    useEffect(() => {
      if (dynamicRef.current) {
        dynamicRef.current.focus();
      }
    }, []);

    useEffect(() => {
      const loadData = async () => {
        try {
          // Load other states from localStorage as before
          const updateStateFromLocalStorage = (key, setter) => {
            const storedValue = localStorage.getItem(key);
            if (storedValue) {
              const parsedValue = JSON.parse(storedValue);
              if (parsedValue) {
                setter(parsedValue);
              }
            }
          };

          // Update states from localStorage
          updateStateFromLocalStorage(
            `prompt_combinations_${nodeId}`,
            setInitial_prompt
          );
          updateStateFromLocalStorage(`llm_prompts_${nodeId}`, set_llm_prompts);
          updateStateFromLocalStorage(`csvData_${nodeId}`, setCsvData);
          updateStateFromLocalStorage(`dataset_${nodeId}`, setDatasetName);

          // Update botData with the new values
          setBotData((prevBotData) => ({
            ...prevBotData,
            workflow: {
              ...prevBotData.workflow,
              [`${nodeId}`]: {
                ...prevBotData.workflow[`${nodeId}`],
                llm_params: {
                  ...(prevBotData.workflow?.[`${nodeId}`]?.llm_params || {}),
                  prompts: llm_prompts,
                },
              },
            },
          }));
        } catch (error) {
          console.error("Error loading data:", error);
        }
      };

      loadData();
    }, [nodeId, setBotData]);

    const removeQApair = (index) => {
      setFewShotQA((prevData) => prevData.filter((_, i) => i !== index));
    };
    const handleStructuredFileRemove = () => {
      setBotData((prevData) => ({
        ...prevData,
        dynamicShot_dataset: {},
      }));
      setCsvData({});

      setDatasetName("");
      setDataset({});

      setOpenDatasetModal(false);
      setResetInput(true);
      setTimeout(() => {
        setResetInput(false);
      }, 100);
    };
    const handleCSVUpload = (event) => {
      const file = event.target.files[0];
      if (!file) return; // Return early if no file is selected

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const lines = content.split("\n");

        if (lines.length < 2) {
          // No rows or only header row present
          data.showSnackbar(
            "The uploaded CSV file must contain a 'question' and 'answer' header along with data rows.",
            "error"
          );
          return;
        }

        // Check for headers
        const headers = lines[0]
          .split(",")
          .map((header) => header.trim().toLowerCase());
        const hasQuestionHeader = headers.includes("question");
        const hasAnswerHeader = headers.includes("answer");

        if (!hasQuestionHeader || !hasAnswerHeader) {
          // Missing required headers
          data.showSnackbar(
            "The uploaded CSV file must have 'question' and 'answer' as column headers.",
            "error"
          );
          return;
        }

        // Process rows while ignoring empty or invalid ones
        const newQAPairs = lines
          .slice(1) // Skip the header row
          .map((line) => {
            const [question, answer] = line.split(",");
            if (question?.trim() && answer?.trim()) {
              return { question: question.trim(), answer: answer.trim() };
            }
            return null; // Ignore invalid or empty rows
          })
          .filter((pair) => pair); // Filter out null values

        if (newQAPairs.length === 0) {
          data.showSnackbar(
            "Few Shot Dataset must contain valid 'question' and 'answer' fields!",
            "error"
          );
          return;
        }

        setFewShotQA((prevData) => {
          if (prevData.length >= 10) {
            data.showSnackbar("Maximum 10 Q&A pairs allowed!", "error");
            return prevData; // Return current data if maximum limit is reached
          }

          const remainingSlots = 10 - prevData.length;
          const newItems = newQAPairs.slice(0, remainingSlots);

          setfewShotReset(true);
          setTimeout(() => {
            setfewShotReset(false);
          }, 100);

          return [...prevData, ...newItems];
        });
      };

      reader.readAsText(file);
    };

    const handlePromptGeneration = async () => {
      const _localdata_ = JSON.parse(localStorage.getItem("problem_statement"));
      set_loader(true);

      // Assuming user is an object that has a nested user object with _id
      const user_id = user?.user?._id["$oid"];
      const unFilteredPrompts = llm_prompts;
      const prompt_types = Object.keys(unFilteredPrompts).filter(
        (_) => unFilteredPrompts[_] === true
      );

      for (const prompt_type of prompt_types) {
        if (
          prompt_type === "few-shot" &&
          (!fewShotQA || fewShotQA.length === 0)
        ) {
          data.showSnackbar(
            "No Dataset uploaded for Few-shot prompt. Please upload dataset before generating prompts.",
            "warning"
          );
          set_loader(false);
          return;
        }

        if (
          prompt_type === "dynamic-shot-random" &&
          (!dataset || Object.keys(dataset).length === 0)
        ) {
          data.showSnackbar(
            "No Dataset uploaded for Dynamic Few Shot prompt. Please upload dataset before generating prompts.",
            "warning"
          );
          set_loader(false);
          return;
        }
      }

      await setBotData((prevData) => ({ ...prevData, rqa_prompt: "" }));
      setInitial_prompt("");

      let prompt_combinations = {};
      let saved_prompts = {};
      let retrieval_check = false;
      // Assuming botData.workflow is your object
      const workflow = botData.workflow;

      const keys = Object.keys(workflow);
      const retrievalKeys = keys.filter((key) => key.startsWith("retrieval_"));
      const generationKeys = keys.filter((key) =>
        key.startsWith("generation_")
      );
      if (retrievalKeys.length > 0 && generationKeys.length > 0) {
        const firstRetrievalKey = retrievalKeys[0];
        const firstGenerationKey = generationKeys[0];

        const retrievalIndex = keys.indexOf(firstRetrievalKey);
        const generationIndex = keys.indexOf(firstGenerationKey);

        if (retrievalIndex < generationIndex) {
          retrieval_check = true;
        } else {
          retrieval_check = false;
        }
      } else {
        if (retrievalKeys.length > 0) {
          retrieval_check = true;
        } else {
          retrieval_check = false;
        }
      }

      const formData = new FormData();

      for (const prompt_type of prompt_types) {
        const references_text =
          JSON.parse(localStorage?.getItem("references_text"))?.replaceAll(
            '"',
            ""
          ) || "";
        const sources_text =
          localStorage?.getItem("sources_text")?.replaceAll('"', "") || "";
        const tone_text =
          localStorage?.getItem("tone_text")?.replaceAll('"', "") || "";
        const prompt_text = references_text + sources_text + tone_text;

        let _data = {
          problem_statement: _localdata_ || "I want a helpful assistant.",
          prompt_type,
          prompt_text,
        };

        if (prompt_type === "few-shot") {
          _data["qa_dict"] = fewShotQA;
        }

        if (prompt_type === "dynamic-shot-random") {
          _data["dataset"] = dataset;
        }

        if (retrieval_check) {
          _data["retrieval"] = "true";
        }

        for (const [key, value] of Object.entries(_data)) {
          if (key === "dataset") {
            formData.append(key, JSON.stringify(dataset));
            continue;
          } else if (key === "prompt_text") {
            formData.append(key, JSON.stringify(value));
            continue;
          } else if (key === "qa_dict") {
            formData.append(key, JSON.stringify(value));
            continue;
          }

          formData.append(key, value);
        }

        for (const entry of formData.entries()) {
        }

        // The API endpoint, assuming `user_id` needs to be part of the URL
        const apiEndpoint = `/api/eval_bot/generate/prompt/${prompt_type}/${user_id}`;
        const token = JSON.parse(localStorage.getItem("token"));

        // Making the POST request
        await axios
          .post(apiEndpoint, formData, {
            headers: {
              // Include the bearer token in the authorization header
              Authorization: `Bearer ${token}`,
            },
          })
          .then((response) => {
            // Handle success

            const prompt = response.data.prompt;

            prompt_combinations[prompt_type] = prompt;
            saved_prompts[prompt_type] = prompt;
          })
          .catch((error) => {
            // Handle error
            console.error("Error:", error);
            sendBugReport(
              organization?.organization,
              user?.user,
              error,
              "Prompt Generation",
              dispatch
            );
          });
      }
      let prompt_str = "";

      prompt_str = prompt_str.replaceAll('"', "");
      setInitial_prompt(prompt_combinations);

      setBotData((prevData) => ({
        ...prevData,
        rqa_prompt: prompt_str,
        prompt_combinations: { ...prompt_combinations },
      }));
      set_loader(false);
      localStorage.setItem(
        `prompt_combinations_${nodeId}`,
        JSON.stringify(prompt_combinations)
      );
      localStorage.setItem(`rqa_prompt_${nodeId}`, JSON.stringify(prompt_str));
    };

    const handleChangeBasePrompt = (e) => {
      setInitial_prompt((prevData) => {
        localStorage.setItem(
          `prompt_combinations_${nodeId}`,
          JSON.stringify({ ...prevData, [e.target.name]: e.target.value })
        );
        return { ...prevData, [e.target.name]: e.target.value };
      });
      setBotData((prevData) => ({
        ...prevData,
        prompt_combinations: {
          ...prevData.prompt_combinations,
          [e.target.name]: e.target.value,
        },
      }));
    };

    const menuClick = (event, type) => {
      const prompts = { ...llm_prompts };
      prompts[type] = event.target.checked;

      if (!Object.values(prompts).some((value) => value)) {
        prompts["zero-shot"] = true;
      }

      set_llm_prompts({ ...prompts });
      setBotData((prevData) => ({
        ...prevData,
        workflow: {
          ...prevData.workflow,
          [`${nodeId}`]: {
            llm_params: {
              ...(prevData.workflow?.[`${nodeId}`]?.llm_params || {}),
              prompts: prompts,
            },
            finetuning_values: {
              ...(prevData.workflow?.[`${nodeId}`]?.finetuning_values || {}),
              "llama-3-8b": {
                ...prevData.workflow[`${nodeId}`].finetuning_values[
                  "llama-3-8b"
                ],
                prompts: { ...prompts },
              },
            },
          },
        },
      }));
      localStorage.setItem(`llm_prompts_${nodeId}`, JSON.stringify(prompts));
    };

    const handleDatasetUpload = (e) => {
      const files = e.target.files;
      if (!files) {
        return;
      }

      for (const file of files) {
        let _VALIDATION_ERROR_ = false;
        const reader = new FileReader();
        const size = file.size / (1024 * 1024); // Convert size to MB

        Papa.parse(file, {
          complete: (result) => {
            const file_data = result.data;
            const headers = Object.keys(file_data[0]);

            if (headers[0] !== "question" || headers[1] !== "answer") {
              _VALIDATION_ERROR_ = true;
              data.showSnackbar(
                "Dynamic Few Shot Dataset must have 'question' and 'answer' headers!",
                "error"
              );
              return;
            }
            if (!_VALIDATION_ERROR_) {
              setCsvData((prevData) => ({
                ...prevData,
                [file.name]: file_data,
              }));
              setBotData((prevData) => ({
                ...prevData,
                dynamicShot_dataset: [
                  ...(Array.isArray(prevData.dynamicShot_dataset)
                    ? prevData.dynamicShot_dataset
                    : []),
                  {
                    filedata: file_data,
                    fileName: file.name,
                    fileType: file.type,
                    category: "",
                    fileSize: size,
                  },
                ],
              }));
              setDataset({
                filedata: file_data,
                fileName: file.name,
                fileType: file.type,
                category: "",
                fileSize: size,
              });
              setDatasetName(file.name);
            }
          },
          header: true,
        });

        reader.onload = async () => {
          if (
            (file.type === "text/csv" ||
              file.type ===
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") &&
            !_VALIDATION_ERROR_
          ) {
          } else {
            _VALIDATION_ERROR_ = true;
            if (
              file.type !== "text/csv" &&
              file.type !==
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ) {
              data.showSnackbar(
                `${file.type} is not a supported file type.\nSupported file types are: CSV, XLSX`,
                "error"
              );
            }
          }
        };

        // Read the file
        if (file.type === "text/csv") {
          reader.readAsText(file);
        } else if (
          file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ) {
          reader.readAsArrayBuffer(file);
        }
      }

      setResetInput(true);
      setTimeout(() => {
        setResetInput(false);
      }, 100);
    };

    return (
      <Box
        data-tour="option-2i"
        sx={{
          opacity: LLMFoundational === true ? "1" : "0.7",
          px: "10px",
          display: "flex",
          flexDirection: "column",
          height: "auto",
          overflowY: "auto",
          flex: 1,
          width: "60%",
          ...scrollbarStyles,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              border: `1px solid ${theme.border.light}`,

              borderRadius: "10px",
            }}
          >
            <Box sx={topNavNode}>
              <Typography sx={childNodeNavText}>
                {" "}
                Prompt for Foundation LLMs
              </Typography>
              <div>
                <InfoOutlined
                  onClick={(event) => handleOpenSideBar("generation", event)}
                />
              </div>
            </Box>
            <Box
              sx={{
                padding: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                pt: 1,
              }}
            >
              {[
                "zero-shot",
                "few-shot",
                "few-shot-random",
                "dynamic-shot-random",
                "chain-of-thought",
              ].map((type) => (
                <Checkbox
                  key={type}
                  label={
                    type
                      .replace(/-/g, " ") // Convert hyphens to spaces
                      .replace(/\b\w/g, (match) => match.toUpperCase()) // Capitalize each word
                  }
                  checked={llm_prompts[type]}
                  onChange={(e) => menuClick(e, type)}
                  customStyles={{ color: theme.textColors.dark.primary }}
                  disabled={
                    !LLMFoundational ||
                    (organization?.organization?.subscription === "starter" &&
                      type !== "zero-shot")
                  }
                  tooltipText={
                    type === "few-shot" &&
                    organization?.organization?.subscription === "starter"
                      ? "You need Pro subscription to access this feature."
                      : "To update the initial prompt, please click the 'Generate Prompt' button."
                  }
                  tooltipPlacement="left"
                />
              ))}
              {/* Few Shot Inputs (Conditional Rendering) */}
              {llm_prompts?.["dynamic-shot-random"] && LLMFoundational && (
                <Tooltip
                  title="Dynamic few shot dataset should have only 'question' and 'answer' columns"
                  placement="left"
                >
                  <div className="few-shot-inputs">
                    <CustomInput
                      ref={dynamicRef}
                      resetInput={resetInput}
                      disable={!LLMFoundational}
                      placeholder={"Upload Dynamic few shot CSV"}
                      type={"file"}
                      upload_type={"dynamicShot"}
                      multiple={true}
                      onHandleChange={handleDatasetUpload}
                    />

                    <p id="info">
                      *{Object.keys(dataset).length > 0 ? 1 : 0} files uploaded
                      for Dynamic shot
                    </p>
                  </div>
                </Tooltip>
              )}

              {Object.keys(dataset).length > 0 && (
                <Box
                  sx={{
                    p: 1,
                    border: `2px solid ${theme.border.divider}`,
                    borderRadius: 2,
                    bgcolor: theme.backgroundColors.dark.secondary,
                  }}
                >
                  <Typography
                    sx={{
                      alignSelf: "center",
                      color: theme.textColors.dark.primary,
                      fontSize: theme.typography.fontSize.xs,
                    }}
                  >
                    Dynamic Few Shot Dataset: {dataset?.fileName}
                  </Typography>
                  <Button
                    sx={{ ...primaryButtonStyles, padding: "1px 4px" }}
                    onClick={() => setOpenDatasetModal(true)}
                  >
                    View
                  </Button>
                </Box>
              )}

              <Dialog
                fullWidth
                maxWidth="md"
                open={openDatasetModal}
                onClose={() => setOpenDatasetModal(false)}
                PaperProps={{ sx: { ...dialogStyles.dialogRoot } }}
              >
                <DialogTitle sx={dialogStyles.dialogTitle}>
                  <IconButton
                    onClick={() => setOpenDatasetModal(false)}
                    sx={dialogStyles.dialogCloseIcon}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                  <Typography sx={dialogStyles.dialogTitleText}>
                    Dynamic Few Shot Dataset
                  </Typography>
                </DialogTitle>
                {Object.keys(csvData).length > 0 && (
                  <DialogActions sx={dialogStyles.dialogActions}>
                    <Button
                      onClick={() => handleStructuredFileRemove(dataset)}
                      sx={deleteButtonStyles}
                    >
                      Delete dataset
                    </Button>
                  </DialogActions>
                )}

                <DialogContent
                  sx={{
                    mt: 2,
                    ...dialogStyles.dialogContent,
                  }}
                >
                  {Object.keys(csvData).length === 0 && (
                    <DialogContentText sx={dialogStyles.dialogContentText}>
                      No data uploaded for Dynamic few shot
                    </DialogContentText>
                  )}
                  <TableContainer sx={tableStyles.tableContainer}>
                    <Table>
                      <TableHead sx={tableStyles.tableHead}>
                        {csvData[datasetName] &&
                          csvData[datasetName].length > 0 && (
                            <TableRow>
                              {csvData[datasetName] &&
                                (
                                  Object.keys(csvData[datasetName][0]) || []
                                )?.map(
                                  (header, index) =>
                                    (header === "question" ||
                                      header === "answer") && (
                                      <TableCell
                                        sx={tableStyles.tableHeaderCell}
                                        key={index}
                                      >
                                        {header}
                                      </TableCell>
                                    )
                                )}
                            </TableRow>
                          )}
                      </TableHead>
                      <TableBody>
                        {csvData[datasetName]?.map(
                          (row, rowIndex) =>
                            rowIndex !== 0 && (
                              <TableRow
                                key={rowIndex}
                                sx={tableStyles.tableRow}
                              >
                                {Object?.values(row)?.map(
                                  (value, cellIndex) =>
                                    (value.length > 0 || value !== "") && (
                                      <Tooltip title={value}>
                                        <TableCell
                                          sx={tableStyles.tableCell}
                                          key={cellIndex}
                                        >
                                          {value.length < 100
                                            ? value
                                            : value.substring(0, 100) + "..."}
                                        </TableCell>
                                      </Tooltip>
                                    )
                                )}
                              </TableRow>
                            )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DialogContent>
              </Dialog>

              {llm_prompts?.["few-shot"] && LLMFoundational && (
                <Tooltip
                  title="Few shot dataset should have only 'question' and 'answer' columns"
                  placement="left"
                >
                  <CustomInput
                    ref={fewShotRef}
                    resetInput={fewShotReset}
                    disable={!LLMFoundational}
                    placeholder={"Upload few shot CSV"}
                    type={"file"}
                    upload_type={"dynamicShot"}
                    multiple={true}
                    onHandleChange={handleCSVUpload}
                  />
                  {fewShotQA?.length > 0 && (
                    <Typography
                      sx={{
                        fontSize: createLLMTypography.fontSizes.sectionTitle,
                        color: theme.textColors.dark.primary,
                        fontWeight: "bold",
                      }}
                    >
                      Few Shot QnA
                    </Typography>
                  )}
                  {fewShotQA?.length > 0 && (
                    <Box
                      className="few-shot-inputs"
                      sx={{
                        ...scrollbarStyles,
                        height: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {fewShotQA.map((qa, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "10px",
                            width: "100%",
                          }}
                        >
                          <div className="qna-display">
                            <div className="qna-display_qna">
                              <p>
                                <b>Question: </b> {qa.question}
                              </p>
                              <p>
                                <b>Answer: </b> {qa.answer}
                              </p>
                            </div>
                            <button
                              className="btn-delete-response"
                              onClick={() => removeQApair(index)}
                            >
                              <DeleteOutline fontSize="small" />
                            </button>
                          </div>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Tooltip>
              )}

              <Button
                sx={{
                  ...primaryButtonStyles,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: theme.typography.fontSize.md,
                  fontWeight: theme.typography.fontWeight.bold,
                  mt: 1,
                }}
                disabled={!LLMFoundational}
                onClick={handlePromptGeneration}
              >
                {loader ? "Generating Prompt" : "Generate Prompt"}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              ...nodeInputStyles,
              border: `1px solid ${theme.border.light}`,
              borderRadius: "10px",
            }}
            data-tour="option-2j"
          >
            <Box sx={topNavNode}>
              <Typography sx={childNodeNavText}> Generated Prompts</Typography>
              <div>
                <InfoOutlined
                  onClick={(event) => handleOpenSideBar("generation", event)}
                />
              </div>
            </Box>
            <div className="node_prompts">
              {!loader ? (
                Object.entries(initial_prompt).map(([key, value]) => (
                  <div key={key} className="prompt-container">
                    <Typography
                      sx={{
                        color: theme.textColors.dark.primary,
                        fontSize: theme.typography.fontSize.md,
                      }}
                    >
                      {key}
                    </Typography>
                    <textarea
                      className="prompt-input"
                      value={value}
                      id={key}
                      name={key}
                      onChange={handleChangeBasePrompt}
                      rows="10"
                      disabled={!LLMFoundational}
                    />
                  </div>
                ))
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    p: 2,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </div>
          </Box>
        </Box>

        {/* <Tooltip title="Scroll up" placement="right">
          <div className="scrollup-btn" id="scrollup-btn">
            <IconButton
              onClick={() => {
                const promptScroller = document.getElementById(
                  "prompt-for-foundational-llms"
                );
                if (promptScroller) {
                  promptScroller.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              <ArrowUpwardIcon />
            </IconButton>
          </div>
        </Tooltip> */}
      </Box>
    );
  }
);

export default PromptNode;
